import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import AdminNavbar from "../components/AdminNavbar";
import { motion } from "framer-motion";

const Store = () => {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchStores();
    }, []);

    const fetchStores = async () => {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("accessToken");
        try {
            const response = await axios.get("/admin/stores", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStores(response.data.stores);
        } catch (err) {
            setError("Failed to fetch stores. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (storeid) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this store?");
        if (!confirmDelete) return;

        const token = localStorage.getItem("accessToken");

        try {
            await axios.delete(`/admin/stores/${storeid}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            alert("Store deleted successfully!");
            fetchStores();
        } catch (error) {
            alert("Failed to delete store. Please try again.");
        }
    };

    const columns = [
        {
            name: "Store ID",
            selector: (row) => row.storeid,
            sortable: true,
        },
        {
            name: "Store Name",
            selector: (row) => row.storename,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div>
                    <button style={styles.deleteButton} onClick={() => handleDelete(row.storeid)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <motion.div
            style={styles.dashboardContainer}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            <AdminNavbar />
            <div style={styles.mainContent}>
                <motion.h1
                    style={styles.title}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    Store Management
                </motion.h1>

                <motion.div
                    style={styles.buttonContainer}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={styles.addStoreButton}
                        onClick={() => navigate("/addStore")}
                    >
                        + Add Store
                    </motion.button>
                </motion.div>

                {error && (
                    <motion.p
                        style={styles.error}
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        {error}
                    </motion.p>
                )}

                <motion.div
                    style={styles.storeList}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {loading ? (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ repeat: Infinity, duration: 1 }}
                        >
                            Loading stores...
                        </motion.p>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={stores}
                            pagination
                            highlightOnHover
                            responsive
                        />
                    )}
                </motion.div>
            </div>
        </motion.div>
    );
};

const styles = {
    dashboardContainer: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#f4f4f4",
    },
    mainContent: {
        flex: 1,
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    title: {
        fontSize: "30px",
        fontWeight: "bold",
        marginBottom: "20px",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "20px",
    },
    addStoreButton: {
        backgroundColor: "#007bff",
        color: "white",
        border: "none",
        padding: "12px 24px",
        fontSize: "16px",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
    deleteButton: {
        backgroundColor: "#dc3545",
        color: "white",
        border: "none",
        padding: "8px 12px",
        fontSize: "14px",
        borderRadius: "5px",
        cursor: "pointer",
        transition: "background 0.3s",
    },
    storeList: {
        width: "80%",
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        textAlign: "center",
    },
    error: {
        color: "red",
        marginBottom: "10px",
        fontWeight: "bold",
    },
};

export default Store;
